/**
 * LoginPage — kirish sahifasi (AvtoFix Design System, professional split layout)
 *
 * Tartib:
 *   ┌──────────────────────┬──────────────────────┐
 *   │  Brand showcase      │  Form card           │
 *   │  (chap, dark accent) │  (o'ng, oq fon)      │
 *   │  - Logo              │  - "Xush kelibsiz"   │
 *   │  - Value prop        │  - Telefon/Username  │
 *   │  - 4 trust signals   │  - Kirish tugmasi    │
 *   │  - Mijozlar soni     │  - Mode toggle       │
 *   └──────────────────────┴──────────────────────┘
 *
 * Mobile: chap blok yashiriladi (lg:hidden), faqat o'ng forma to'liq kenglikda.
 *
 * Auth oqimi:
 *   - Default rejim: telefon + parol (Faza 26 P0-5 kanonik)
 *   - Mode toggle: "Username bilan kirish" (backwards-compat)
 *   - PhoneInput +998 prefix avtomatik qo'yadi
 *   - Login muvaffaqiyatli → `/` ga redirect (SPA root)
 */

import type { LoginInput } from '@avtofix/shared';
import { loginSchema } from '@avtofix/shared';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  Info,
  Loader2,
  Lock,
  MessageCircle,
  Phone,
  ShieldCheck,
  User,
  WifiOff,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';

import { PhoneInput } from '@/components/phone-input';
import { Button } from '@/components/ui/button';
import { useIsAuthenticated, useLogin } from '@/features/auth/auth.hooks';
import { ApiClientError } from '@/shared/lib/api-client';
import { cn } from '@/shared/lib/cn';

type LoginMode = 'phone' | 'username';

const TRUST_POINTS: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  text: string;
}[] = [
  { icon: WifiOff, text: "Offline rejim — internet uzilsa ham sotuv to'xtamaydi" },
  { icon: Zap, text: "5 daqiqada sozlash, hech qanday murakkab integratsiya yo'q" },
  { icon: ShieldCheck, text: "Ma'lumotlaringiz shifrlangan, faqat sizga mansub" },
  { icon: CheckCircle2, text: "24/7 O'zbek tilida qo'llab-quvvatlash" },
];

export function LoginPage(): React.ReactElement {
  const isAuthenticated = useIsAuthenticated();
  const loginMutation = useLogin();
  const [searchParams] = useSearchParams();
  const isSignupIntent = searchParams.get('mode') === 'signup';
  const selectedPlan = searchParams.get('plan');
  const isSessionExpired = searchParams.get('reason') === 'session-expired';
  const [mode, setMode] = useState<LoginMode>('phone');
  const [phone, setPhone] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setValidationError(null);

    const payload: LoginInput =
      mode === 'phone'
        ? ({ phone, password } as LoginInput)
        : ({ username, password } as LoginInput);
    const parsed = loginSchema.safeParse(payload);
    if (!parsed.success) {
      const firstError = parsed.error.errors[0];
      setValidationError(firstError?.message ?? "Maydonlar to'ldirilmagan");
      return;
    }

    loginMutation.mutate(parsed.data);
  };

  const errorMessage =
    validationError ??
    (loginMutation.error instanceof ApiClientError
      ? loginMutation.error.message
      : loginMutation.error
        ? "Ulanishda xato. Qayta urinib ko'ring."
        : null);

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.05fr_1fr]">
      {/* ─── CHAP — Brand showcase (lg+ faqat) ──────────────────────── */}
      <BrandPanel />

      {/* ─── O'NG — Login form ─────────────────────────────────────── */}
      <div className="relative flex min-h-dvh flex-col overflow-y-auto bg-background lg:min-h-0">
        {/* Top utility bar */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 sm:px-10 sm:py-5">
          <Link
            to="/"
            className="inline-flex min-h-[44px] items-center gap-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="size-4 shrink-0" strokeWidth={1.75} />
            Bosh sahifaga
          </Link>
          <span className="flex min-h-[44px] items-center text-right text-[12px] leading-tight text-muted-foreground">
            <span className="hidden sm:inline">Hisobingiz yo&apos;qmi?&nbsp;</span>
            <Link
              to="/demo"
              className="font-semibold text-primary underline underline-offset-[3px] hover:text-primary/80"
            >
              Bepul boshlash
            </Link>
          </span>
        </div>

        <div className="flex flex-1 items-start justify-center px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-2 sm:items-center sm:px-10 sm:py-8">
          <div className="w-full max-w-[420px]">
            {/* Mobile-only mini brand (chap blok yashirin) */}
            <div className="mb-6 flex items-center sm:mb-7 lg:hidden">
              <span className="inline-flex items-center gap-2 text-xl font-extrabold tracking-tight sm:text-2xl">
                <span className="flex size-8 items-center justify-center rounded-md bg-primary text-white sm:size-9">
                  <Phone className="size-4 sm:size-[18px]" strokeWidth={2.5} />
                </span>
                <span className="text-foreground">
                  Avto<span className="text-primary">Fix</span>
                </span>
              </span>
            </div>

            <h1 className="text-2xl font-bold leading-[1.15] tracking-tight text-foreground sm:text-3xl">
              {isSignupIntent ? "Ro'yxatdan o'tish" : 'Xush kelibsiz'}
            </h1>
            <p className="mt-2 text-[14px] text-muted-foreground sm:text-[15px]">
              {isSignupIntent
                ? 'AvtoFix ga xush kelibsiz! Hisob ochish uchun biz bilan bog\u2019laning.'
                : mode === 'phone'
                  ? 'Telefon raqamingiz va parol bilan kiring.'
                  : 'Username va parol bilan kiring.'}
            </p>

            {/* Session-expired banner — M9 fix */}
            {isSessionExpired && (
              <div
                role="alert"
                className="mt-5 flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/5 px-4 py-3"
              >
                <AlertCircle
                  className="mt-0.5 size-5 shrink-0 text-warning"
                  strokeWidth={2}
                  aria-hidden
                />
                <div>
                  <p className="text-[13px] font-semibold text-warning">Sessiya muddati tugadi</p>
                  <p className="mt-0.5 text-[12.5px] text-muted-foreground">
                    Xavfsizlik uchun tizimdan chiqarildingiz. Qayta kiring.
                  </p>
                </div>
              </div>
            )}

            {/* Signup banner — ro'yxat faqat telefon orqali */}
            {isSignupIntent && (
              <div className="mt-5 rounded-xl border border-primary/30 bg-primary/5 p-4">
                <div className="flex items-start gap-3">
                  <Info
                    className="mt-0.5 size-5 shrink-0 text-primary"
                    strokeWidth={2}
                    aria-hidden
                  />
                  <div className="flex-1">
                    <p className="text-[13px] font-semibold text-foreground">
                      {selectedPlan === 'pro'
                        ? 'Pro tarif — 14 kun bepul sinov'
                        : selectedPlan === 'start'
                          ? 'Start tarif — 14 kun bepul sinov'
                          : '14 kun bepul sinov'}
                    </p>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
                      Do&apos;koningiz uchun hisob ochamiz, sozlaymiz va bepul o&apos;rnatamiz.
                      Bizga qo&apos;ng&apos;iroq qiling yoki Telegram&apos;da yozing — 2 daqiqa
                      ichida javob beramiz.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <a
                        href="tel:+998901234567"
                        className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3 text-[12.5px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                      >
                        <Phone className="size-3.5" strokeWidth={2.5} />
                        +998 90 123 45 67
                      </a>
                      <a
                        href="https://t.me/avtofix_support"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-[12.5px] font-semibold text-foreground transition-colors hover:bg-accent"
                      >
                        <MessageCircle className="size-3.5" strokeWidth={2} />
                        Telegram orqali
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 space-y-4 sm:mt-7">
              {/* Phone / Username (mode ga qarab) */}
              {mode === 'phone' ? (
                <div className="space-y-1.5">
                  <label
                    htmlFor="phone"
                    className="block text-[13px] font-semibold text-foreground"
                  >
                    Telefon raqami
                  </label>
                  <PhoneInput
                    id="phone"
                    value={phone}
                    onChange={setPhone}
                    autoComplete="tel"
                    required
                    disabled={loginMutation.isPending}
                    aria-label="Telefon raqami"
                    className="h-12 rounded-lg sm:h-11"
                  />
                </div>
              ) : (
                <div className="space-y-1.5">
                  <label
                    htmlFor="username"
                    className="block text-[13px] font-semibold text-foreground"
                  >
                    Username
                  </label>
                  <div className="relative">
                    <User
                      className="pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-muted-foreground"
                      strokeWidth={1.75}
                      aria-hidden
                    />
                    <input
                      id="username"
                      type="text"
                      autoComplete="username"
                      required
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                      }}
                      disabled={loginMutation.isPending}
                      className={cn(
                        'h-12 w-full rounded-lg border border-input bg-card pl-10 pr-3 sm:h-11',
                        'text-[16px] font-medium text-foreground outline-none transition-all sm:text-[14px]',
                        'placeholder:text-muted-foreground',
                        'focus:border-primary focus:ring-2 focus:ring-primary/20',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                      )}
                      placeholder="egasi"
                    />
                  </div>
                </div>
              )}

              {/* Password */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-3">
                  <label
                    htmlFor="password"
                    className="block text-[13px] font-semibold text-foreground"
                  >
                    Parol
                  </label>
                  <span className="-my-2 inline-flex min-h-[44px] cursor-pointer items-center text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground sm:min-h-0 sm:py-0">
                    Esdan chiqdimi?
                  </span>
                </div>
                <div className="relative">
                  <Lock
                    className="pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-muted-foreground"
                    strokeWidth={1.75}
                    aria-hidden
                  />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                    }}
                    disabled={loginMutation.isPending}
                    className={cn(
                      'h-12 w-full rounded-lg border border-input bg-card pl-10 pr-12 sm:h-11',
                      'text-[16px] font-medium text-foreground outline-none transition-all sm:text-[14px]',
                      'placeholder:text-muted-foreground',
                      'focus:border-primary focus:ring-2 focus:ring-primary/20',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                    )}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowPassword((v) => !v);
                    }}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Parolni yashirish' : "Parolni ko'rsatish"}
                    className="absolute right-2 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="size-[18px]" strokeWidth={1.75} />
                    ) : (
                      <Eye className="size-[18px]" strokeWidth={1.75} />
                    )}
                  </button>
                </div>
              </div>

              {/* Error */}
              {errorMessage !== null && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-[13px] text-destructive"
                >
                  <AlertCircle className="mt-0.5 size-4 shrink-0" strokeWidth={2} aria-hidden />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                size="lg"
                disabled={loginMutation.isPending}
                className="h-12 w-full text-[15px] sm:h-11"
              >
                {loginMutation.isPending ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Tekshirilmoqda...
                  </>
                ) : (
                  <>
                    Kirish
                    <ArrowRight />
                  </>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="mt-6 flex items-center gap-3 sm:mt-7">
              <span className="h-px flex-1 bg-border" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Yoki
              </span>
              <span className="h-px flex-1 bg-border" />
            </div>

            {/* Mode toggle */}
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => {
                setMode(mode === 'phone' ? 'username' : 'phone');
                setValidationError(null);
              }}
              className="mt-5 h-12 w-full sm:h-11"
            >
              {mode === 'phone' ? (
                <>
                  <User />
                  Username bilan kirish
                </>
              ) : (
                <>
                  <Phone />
                  Telefon bilan kirish
                </>
              )}
            </Button>

            <p className="mt-6 text-center text-[11px] leading-relaxed text-muted-foreground">
              Kirish orqali siz{' '}
              <span className="font-medium text-foreground/70 underline underline-offset-2">
                Foydalanish shartlari
              </span>{' '}
              va{' '}
              <span className="font-medium text-foreground/70 underline underline-offset-2">
                Maxfiylik siyosati
              </span>
              ni qabul qilasiz.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   BrandPanel (chap) — dark slate fonida AvtoFix brendi
   ══════════════════════════════════════════════════════════════════════ */

function BrandPanel(): React.ReactElement {
  return (
    <aside
      className="relative hidden overflow-hidden lg:flex lg:flex-col"
      style={{
        background:
          'radial-gradient(circle at 0% 0%, hsl(220 30% 16%) 0%, hsl(220 25% 11%) 60%, hsl(220 20% 8%) 100%)',
      }}
    >
      {/* Dekorativ grid pattern */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
      {/* Subtle red corner glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 -top-32 size-96 rounded-full bg-primary/15 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -left-20 size-96 rounded-full bg-primary/10 blur-3xl"
      />

      {/* Top — brand mark */}
      <div className="relative px-12 pt-12">
        <Link
          to="/"
          className="inline-flex items-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          aria-label="AvtoFix bosh sahifa"
        >
          <span className="inline-flex items-center gap-2.5">
            <span className="flex size-10 items-center justify-center rounded-xl bg-white shadow-lg">
              <Phone className="size-5 text-primary" strokeWidth={2.5} />
            </span>
            <span className="text-2xl font-extrabold tracking-tight text-white">
              Avto<span className="text-primary">Fix</span>
            </span>
          </span>
        </Link>
      </div>

      {/* Center — value proposition */}
      <div className="relative flex flex-1 flex-col justify-center px-12 py-10">
        <p className="inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/80">
          <span className="size-1.5 rounded-full bg-success" />
          POS · Ombor · CRM
        </p>
        <h2 className="mt-5 text-[clamp(1.75rem,3.2vw,2.5rem)] font-extrabold leading-[1.1] tracking-tight text-white">
          Avto ehtiyot qismlar do&apos;koni uchun{' '}
          <span className="text-primary">to&apos;liq tizim</span>
        </h2>
        <p className="mt-5 max-w-md text-[15px] leading-relaxed text-white/70">
          Kassa, ombor, mijozlar, qarzlar, statistika — barchasi bitta dasturda. Offline ishlaydi,
          O&apos;zbek tilida, 5 daqiqada sozlanadi.
        </p>

        {/* Trust list */}
        <ul className="mt-8 space-y-3">
          {TRUST_POINTS.map((p) => (
            <li key={p.text} className="flex items-start gap-3 text-[14px] text-white/80">
              <p.icon className="mt-0.5 size-4 shrink-0 text-success" strokeWidth={2} aria-hidden />
              <span>{p.text}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Bottom — social proof */}
      <div className="relative border-t border-white/10 px-12 py-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {['SR', 'NS', 'BM', 'JK'].map((initials, idx) => (
                <div
                  key={initials}
                  className={cn(
                    'flex size-8 items-center justify-center rounded-full border-2 border-[hsl(220_25%_11%)] text-[10px] font-bold text-white shadow-sm',
                    idx === 0 && 'bg-[hsl(215_60%_50%)]',
                    idx === 1 && 'bg-[hsl(330_65%_55%)]',
                    idx === 2 && 'bg-[hsl(142_60%_40%)]',
                    idx === 3 && 'bg-[hsl(38_82%_50%)]',
                  )}
                >
                  {initials}
                </div>
              ))}
            </div>
            <div>
              <p className="text-[13px] font-semibold text-white">2 500+ avto do&apos;kon</p>
              <p className="text-[11px] text-white/60">Bizga ishonib, sotuvni avtomatlashtirgan</p>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
